/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/**
 * Alert feature related request structures are defined here
 */
export const GRANULARITY = {
  DAILY: 'Date',
  WEEKLY: 'Week of Year',
  MONTHLY: 'Month',
  YEARLY: 'Year',
};

export const recurrenceOptions = [
  { label: 'None', value: 'static' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Fortnightly', value: 'fortnightly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Annually', value: 'annually' },
];

export const targetDateTypes = [{ label: 'Stay Date', value: 'stay_date' }];

export const API = {
  GET_ALERT_TABLE_DATA: {
    path: '/organizations/:organizationId/users/:userId/alert-data?:query',
    method: 'GET',
  },
  POST_ALERT: {
    path: '/organizations/:organizationId/alerts',
    method: 'POST',
  },
  PATCH_ALERT: {
    path: '/organizations/:organizationId/alerts/:alertId',
    method: 'PATCH',
  },
  DELETE_ALERT: {
    path: '/organizations/:organizationId/alerts/:alertId',
    method: 'DELETE',
  },
  GET_ALERT_PARAM: {
    path: '/alert-parameters?:query',
    method: 'GET',
  },
  GET_ALERT_ATTACHMENT_DATA: {
    path: '/alert-attachment-data?:query',
    method: 'GET',
  },
  GET_ALERT_LOG_TABLE_DATA: {
    path: '/organizations/:organizationId/users/:userId/alert-notification?:query',
    method: 'GET',
  },
  PATCH_USER_SUBSCRIPTIONS: {
    path: '/organizations/:organizationId/subscriptions/:userId',
    method: 'PATCH',
  },
  GET_USER_SUBSCRIPTIONS: {
    path: '/organizations/:organizationId/subscriptions/:userId/?:query',
    method: 'GET',
  },
  GET_ALERT_ATTACHMENT_EXCEL: {
    path: '/organizations/:organizationId/notification/:notificationId/alert/:alertId/download-excel?:query',
    method: 'GET',
  },
};

export const ALERT_NAME_MAP = {
  pace_audit_vs_stlp: 'Pace-in-Brief',
};

export const CONFIGURE_TYPE = {
  AUDIT: 'Audit',
  ALERT: 'Alert',
};

export const AUDIT_TABLE_PAGE_SIZE = 10;

// Semantic PDF export layout (jsPDF A4 defaults: 210mm x 297mm)
export const PDF_MARGIN_X = 14;
export const PDF_TOP_Y = 20;
export const PDF_BOTTOM_LIMIT = 277; // A4 height (297mm) minus bottom margin
export const PDF_TEXT_WIDTH = 210 - PDF_MARGIN_X * 2;

// Semantic PDF typography mirrors the drawer's ReactMarkdown component styles
// (mdComponents in components/log-drawer.jsx) so the exported report looks like
// the on-screen remarks. CSS px map to pt at 0.75pt per px.
export const PDF_BODY_SIZE = 10.5; // UI body/list text: 14px
export const PDF_TABLE_SIZE = 9.5; // UI table cells: 13px
export const PDF_LINE_RATIO = 0.58; // ~1.6 line-height, matching UI p/li
export const PDF_HEADING_SIZES = { 1: 16, 2: 13.5, 3: 12 }; // h2=18px, h3=16px; h4+ falls back to body size
export const PDF_TEXT_COLOR = [55, 65, 81]; // #374151
export const PDF_HEADING_COLOR = [17, 24, 39]; // #111827
export const PDF_LINK_COLOR = [25, 118, 210]; // MUI primary link blue
export const PDF_TABLE_LINE_COLOR = [209, 213, 219]; // #d1d5db
export const PDF_TABLE_HEAD_FILL = [249, 250, 251]; // #f9fafb

export const notificationStatus = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  MUTED: 'MUTED',
};
//
export const semanticAnalysisTrendConfig = {
  typeName: 'Semantic Analysis Trend',
  auditName: 'Semantic Analysis Trend (Revenue, Rooms, ADR)',
};
//
export const semanticAnalysisPickupConfig = {
  typeName: 'Semantic Analysis Pickup',
  auditName: 'Semantic Analysis Pickup (Revenue, Rooms, ADR)',
};
//
export const ALERT_TYPES = {
  pace_audit_vs_stlp: {
    typeName: 'Pace-in-Brief',
    auditName: 'Pace-in-Brief (Revenue, Rooms, ADR)',
  },
  'semantic-analysis-trend': semanticAnalysisTrendConfig,
  'semantic-analysis-pickup': semanticAnalysisPickupConfig,
  horizon_audit_vs_stlp: {
    typeName: 'Horizon Briefing',
    auditName: 'Horizon Briefing (Revenue, Rooms, ADR)',
  },
};

// HACK (temporary): proc whose configuration rows are sub-grouped by the
// normalized report name (see normalizeSemanticAlertName in functions.js) because
// multiple report variants share one alertProc and would otherwise merge into a
// single row. Expected to be refactored once semantic alerts get a proper
// reportType field or a distinct alertProc per report variant.
export const SEMANTIC_NAME_GROUPED_PROC = ['semantic-analysis-trend', 'semantic-analysis-pickup'];
